import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ReactComponent as ArrowDown } from 'assets/icons/triangleDown.svg'
import cn from '../../../utils/cn'
import { SelectOption } from '.'

interface IMultiSelectProps {
  name: string
  options: SelectOption[]
  selectedOptions: SelectOption[]
  onChange: (values: SelectOption[], name: string) => void
  disabled?: boolean
  placeholder?: string
  fullWidth?: boolean
  maxHeight?: number | string
}

const MultiSelect = ({
  name,
  options,
  selectedOptions,
  onChange,
  disabled,
  placeholder = 'Выберите значения',
  fullWidth = true,
  maxHeight = 500,
}: IMultiSelectProps) => {
  const [isOpen, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const wrapperRef = useRef<HTMLDivElement>(null)

  const selectedValues = useMemo(() => new Set(selectedOptions.map((item) => item.value)), [selectedOptions])

  const filteredOptions = useMemo(() => {
    return options.filter((option) => String(option.label).toLowerCase().includes(search.toLowerCase()))
  }, [options, search])

  const toggleOption = (option: SelectOption) => {
    if (selectedValues.has(option.value)) {
      onChange(
        selectedOptions.filter((item) => item.value !== option.value),
        name
      )
    } else {
      onChange([...selectedOptions, option], name)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div ref={wrapperRef} className='etba-select etba-multiselect' style={fullWidth ? { width: '100%' } : {}}>
      <div
        className='etba-input'
        style={fullWidth ? { width: '100%' } : {}}
        onClick={() => !disabled && setOpen((prev) => !prev)}
      >
        {selectedOptions.length
          ? selectedOptions
              .slice(0, 3)
              .map((item) => item.label)
              .join(', ') + (selectedOptions.length > 3 ? ` +${selectedOptions.length - 3}` : '')
          : placeholder}
      </div>

      {!disabled && (
        <ArrowDown
          className={cn('etba-select-arrow', {
            'is-open': isOpen,
          })}
        />
      )}

      {isOpen && (
        <div
          className='etba-select-menu etba-multiselect-menu'
          style={{
            maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
            overflowY: 'auto',
          }}
        >
          <div style={{ padding: 8 }}>
            <input
              type='text'
              placeholder='Поиск...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                padding: '6px 8px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {filteredOptions.map((option) => {
            const selected = selectedValues.has(option.value)

            return (
              <div
                key={String(option.value)}
                className={cn('etba-select-item', {
                  'is-selected': selected,
                })}
                onClick={() => toggleOption(option)}
              >
                <input type='checkbox' checked={selected} readOnly style={{ marginRight: 8 }} />
                {option.label}
              </div>
            )
          })}

          {filteredOptions.length === 0 && <div style={{ padding: 8, textAlign: 'center' }}>Ничего не найдено</div>}
        </div>
      )}
    </div>
  )
}

export default MultiSelect
